import { addDaysYmd, localDateOf } from './mcp/timezone';

/**
 * The shop leaderboard (Sonar-experience D6): four operational numbers per person over a
 * week or a month, and the ONE report in the product a tech is allowed to read.
 *
 * Deliberately not a sixteenth entry in `REPORT_KEYS`. That catalogue is closed, every
 * member of it is `report:read` — office, owner, bookkeeper — and each one takes a
 * `from`/`to` and answers in `Record<string, ReportCell>`. This takes a NAMED WINDOW, ships
 * typed rows the fleet page can render without stringly-typed lookups, and is gated on a
 * separate verb precisely so that letting a tech see the board never means letting them see
 * the P&L. See `report:read-board` in `constants/roles.ts`.
 *
 * ⭐ Every figure below travels with the basis that produced it. A board is a comparison
 * between people, which is the kind of number that gets argued about — "23 jobs" is not an
 * answer to anything until you can say over what window, in whose clock, counted how, and
 * who else was eligible to be on the list.
 */

export const LEADERBOARD_WINDOWS = ['week', 'month'] as const;
export type LeaderboardWindow = (typeof LEADERBOARD_WINDOWS)[number];

/**
 * The window resolved to inclusive org-local calendar dates.
 *
 * Two rules, both stated here because both are arguable and neither should be re-decided
 * per surface:
 *
 *  1. **The week starts SUNDAY.** US shops, US product. Sunday is also the only day on
 *     which the two conventions visibly disagree, so it is where the test lives.
 *  2. **Both windows run TO DATE** — `to` is the shop's today, never the end of the
 *     calendar period. Same reading as `presetRange('mtd')`, and the only honest one for a
 *     board on a live dashboard: a month window that ran to the 31st would be quietly
 *     promising rows for days that have not happened.
 *
 * `tz` is not a nicety and `now` is injectable for exactly one reason: built from UTC parts,
 * "this week" for a New York shop rolls over at 8 PM on Saturday, and "this month" at 8 PM
 * on the 31st — the evening an owner is most likely to look. That defect has already been
 * paid for once in `presetRange`; this is the same arithmetic, so it gets the same guard.
 *
 * Calendar arithmetic on strings, never on a `Date` in the shop's zone: "which Sunday opened
 * this week" is a question about a calendar, and the moment a `Date` is involved it acquires
 * an instant, an offset and a DST rule the question does not have. (A week containing a
 * spring-forward Sunday is still seven calendar days; measured as an interval it is 167
 * hours, which is how a DST week loses a day.)
 */
export function leaderboardRange(
  window: LeaderboardWindow,
  tz: string,
  now: Date = new Date(),
): { from: string; to: string } {
  const today = localDateOf(now, tz);
  if (window === 'month') {
    return { from: `${today.slice(0, 7)}-01`, to: today };
  }
  return { from: addDaysYmd(today, -weekdayOf(today)), to: today };
}

/**
 * Day of week for a calendar date, Sunday = 0.
 *
 * `Date.UTC` is used as a pure calendar function — the value never leaves this expression
 * and no zone is applied to it, so the answer is a property of the DATE and not of any
 * instant. Reading a zone in here is what would break it.
 */
function weekdayOf(ymd: string): number {
  const [y, m, d] = ymd.split('-').map(Number) as [number, number, number];
  return new Date(Date.UTC(y, m - 1, d)).getUTCDay();
}

/** What produced the numbers. Rendered beside them, never inferred by a caller. */
export interface LeaderboardBasis {
  window: LeaderboardWindow;
  /** Inclusive org-local calendar dates the window resolved to. */
  from: string;
  to: string;
  /** The shop's IANA zone those dates were read in — the one clock. */
  tz: string;
  /** The shop's calendar date when this ran. */
  today: string;
  /** Who was eligible to appear, in a sentence — a filtered list states its population. */
  population: string;
  /** Whether tips are inside `collectedCents`. They are not, and it says so out loud. */
  tipsIncluded: boolean;
}

/**
 * One person's line.
 *
 * ⭐ Every percentage ships with the count it was taken over. A board that prints "100%"
 * over a denominator of one is not wrong, it is unreadable — and `null` over a denominator
 * of zero is the acceptance criterion this ticket was written around: a shop that has never
 * recorded an arrival must see "—", never a perfect score it did not earn.
 *
 * No `role`. A tech holds no `member:read`; the board discloses peers' NAMES because it
 * cannot be a board otherwise (the spec names that disclosure and D6 approves it), and
 * carries no other roster FIELD.
 *
 * ⚠️ Not the same as disclosing nothing about the roster, and the difference is worth being
 * exact about rather than absolute: every tech is zero-filled and non-techs appear only when
 * they were active, so a reader can infer that any all-zero row is a tech. That inference is
 * the price of the zero-fill acceptance criterion and it is stated in `basis.population`
 * rather than hidden — but a docblock claiming the board says nothing about the roster would
 * be a comment the code cannot honour.
 */
export interface LeaderboardRow {
  userId: string;
  name: string;
  /** Distinct jobs finished in the window that this person was on. */
  jobsCompleted: number;
  /** Share of measured arrivals inside the promised window, 0–100, or null if none. */
  onTimePct: number | null;
  /** How many arrivals could be measured at all. The denominator, said out loud. */
  onTimeMeasured: number;
  /** Share of finished visits carrying a photo AND every required item ticked. */
  evidencePct: number | null;
  evidenceMeasured: number;
  /** Money this person recorded in the window, tips excluded. */
  collectedCents: number;
}

export interface LeaderboardResult {
  basis: LeaderboardBasis;
  rows: LeaderboardRow[];
  /** What the board cannot tell you, and why. Rendered, not buried. */
  caveats: string[];
}

/**
 * A percentage of a known population, or null when the population is empty; the one share
 * helper, for the board and the Revenue page alike.
 *
 * The null is the whole point: `0/0` is not 100% and it is not 0% either, and both of those
 * answers are ones a person would act on. A denominator below zero is null too: a Revenue
 * total can go negative under Collected (refunds outrunning what arrived), and a share of it
 * would turn every sign around.
 *
 * `decimals` is how finely it is rounded. The board keeps whole numbers (the default): it is
 * read at a glance, and the tenth of a percent is noise between two people's weeks. The Revenue
 * page's shares of its total are to one decimal (15.4% for 30,000 of 195,000).
 */
export function sharePct(
  numerator: number,
  denominator: number,
  decimals = 0,
): number | null {
  if (denominator <= 0) return null;
  const scale = 10 ** decimals;
  return Math.round((numerator / denominator) * 100 * scale) / scale;
}
